/**
 * FormBuilderToolbar Component
 *
 * Top toolbar for the form builder with form name editing, mode toggles,
 * undo/redo, save/cancel, and export/import actions.
 */

import { forwardRef, memo, useCallback, useState, useRef } from 'react';
import {
  Box,
  Group,
  Button,
  ActionIcon,
  Tooltip,
  Text,
  TextInput,
  Divider,
  Menu,
  Badge,
  FileButton,
} from '@mantine/core';
import { Icon } from '@/components/ui/icons/Icon';
import {
  useFormBuilderStore,
  selectForm,
  selectIsEditMode,
  selectShowPreview,
  selectCanUndo,
  selectCanRedo,
  selectFieldCount,
} from '@/stores/formBuilderStore';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';
import { downloadJSON } from './utils';
import type { FormBuilderToolbarProps } from './types';
import classes from './FormBuilder.module.css';

/**
 * FormBuilderToolbar Component
 */
const FormBuilderToolbar = forwardRef<HTMLDivElement, FormBuilderToolbarProps>(
  ({ onSave, onCancel, className, extra }, ref) => {
    const form = useFormBuilderStore(selectForm);
    const isEditMode = useFormBuilderStore(selectIsEditMode);
    const showPreview = useFormBuilderStore(selectShowPreview);
    const canUndo = useFormBuilderStore(selectCanUndo);
    const canRedo = useFormBuilderStore(selectCanRedo);
    const fieldCount = useFormBuilderStore(selectFieldCount);

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const {
      updateFormName,
      toggleEditMode,
      togglePreview,
      undo,
      redo,
      setShowFormSettings,
      exportFormJSON,
      importFormJSON,
      validateForm,
    } = useFormBuilderStore();

    const [isEditingName, setIsEditingName] = useState(false);
    const [tempName, setTempName] = useState('');
    const nameInputRef = useRef<HTMLInputElement>(null);

    const handleNameClick = useCallback(() => {
      if (form) {
        setTempName(form.name);
        setIsEditingName(true);
        setTimeout(() => nameInputRef.current?.focus(), 0);
      }
    }, [form]);

    const handleNameSubmit = useCallback(() => {
      if (tempName.trim()) {
        updateFormName(tempName.trim());
      }
      setIsEditingName(false);
    }, [tempName, updateFormName]);

    const handleNameKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
          handleNameSubmit();
        } else if (e.key === 'Escape') {
          setIsEditingName(false);
        }
      },
      [handleNameSubmit]
    );

    const handleExport = useCallback(() => {
      if (!form) return;
      const json = exportFormJSON();
      const filename = `${form.name.toLowerCase().replace(/\s+/g, '-')}-form.json`;
      downloadJSON(JSON.parse(json), filename);
    }, [form, exportFormJSON]);

    const handleImport = useCallback(
      (file: File | null) => {
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
          const content = e.target?.result as string;
          const success = importFormJSON(content);
          if (!success) {
            // Could show a notification here
            console.error('Failed to import form');
          }
        };
        reader.readAsText(file);
      },
      [importFormJSON]
    );

    const handleSave = useCallback(() => {
      const { isValid, errors } = validateForm();
      if (!isValid) {
        // Could show validation errors in a modal
        console.error('Form validation failed:', errors);
        return;
      }
      onSave?.();
    }, [validateForm, onSave]);

    if (!form) {
      return null;
    }

    return (
      <Box ref={ref} className={`${classes.toolbar} ${className || ''}`}>
        {/* Left section - Form name */}
        <Box className={classes.toolbarLeft}>
          {isEditingName ? (
            <TextInput
              ref={nameInputRef}
              value={tempName}
              onChange={(e) => setTempName(e.target.value)}
              onBlur={handleNameSubmit}
              onKeyDown={handleNameKeyDown}
              className={classes.formNameInput}
              size="md"
            />
          ) : (
            <Text
              size="lg"
              fw={600}
              onClick={handleNameClick}
              style={{ ...accessibilityStyles, cursor: 'pointer' }}
            >
              {form.name}
            </Text>
          )}
          <Badge variant="light" size="sm">
            {fieldCount} field{fieldCount !== 1 ? 's' : ''}
          </Badge>
        </Box>

        {/* Center section - Mode toggles */}
        <Group className={classes.toolbarCenter} gap="xs">
          <Tooltip label="Undo (Ctrl+Z)">
            <ActionIcon
              variant="subtle"
              size="lg"
              onClick={undo}
              disabled={!canUndo}
            >
              <Icon name="undo" size={18} />
            </ActionIcon>
          </Tooltip>

          <Tooltip label="Redo (Ctrl+Y)">
            <ActionIcon
              variant="subtle"
              size="lg"
              onClick={redo}
              disabled={!canRedo}
            >
              <Icon name="redo" size={18} />
            </ActionIcon>
          </Tooltip>

          <Divider orientation="vertical" />

          <Button.Group>
            <Button
              variant={isEditMode && !showPreview ? 'filled' : 'light'}
              size="sm"
              leftSection={<Icon name="edit" size={16} />}
              onClick={() => {
                if (showPreview) togglePreview();
                if (!isEditMode) toggleEditMode();
              }}
            >
              Edit
            </Button>
            <Button
              variant={showPreview ? 'filled' : 'light'}
              size="sm"
              leftSection={<Icon name="eye" size={16} />}
              onClick={togglePreview}
            >
              Preview
            </Button>
          </Button.Group>

          <Divider orientation="vertical" />

          <Tooltip label="Form Settings">
            <ActionIcon
              variant="subtle"
              size="lg"
              onClick={() => setShowFormSettings(true)}
            >
              <Icon name="settings" size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>

        {/* Right section - Actions */}
        <Group className={classes.toolbarRight} gap="sm">
          <Menu shadow="md" width={200}>
            <Menu.Target>
              <Button variant="subtle" size="sm" rightSection={<Icon name="chevronDown" size={14} />}>
                <Icon name="moreHorizontal" size={16} />
              </Button>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Label>Form Actions</Menu.Label>
              <Menu.Item
                leftSection={<Icon name="download" size={14} />}
                onClick={handleExport}
              >
                Export as JSON
              </Menu.Item>
              <FileButton onChange={handleImport} accept=".json">
                {(props) => (
                  <Menu.Item
                    leftSection={<Icon name="upload" size={14} />}
                    {...props}
                  >
                    Import from JSON
                  </Menu.Item>
                )}
              </FileButton>
              <Menu.Divider />
              <Menu.Item
                leftSection={<Icon name="copy" size={14} />}
                onClick={() => {
                  const json = exportFormJSON();
                  navigator.clipboard.writeText(json);
                }}
              >
                Copy JSON to Clipboard
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>

          {extra}

          {onCancel && (
            <Button variant="subtle" size="sm" onClick={onCancel}>
              Cancel
            </Button>
          )}

          {onSave && (
            <Button
              size="sm"
              leftSection={<Icon name="save" size={16} />}
              onClick={handleSave}
            >
              Save Form
            </Button>
          )}
        </Group>
      </Box>
    );
  }
);

FormBuilderToolbar.displayName = 'FormBuilderToolbar';

export default memo(FormBuilderToolbar);
